import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../../api/client';
import {
  listPrinterProfiles,
  replacePrinterProfileTemplates,
  updateTemplate,
  type LabelTemplate,
  type ManagedPrinterProfile,
} from '../api';
import { Canvas } from './Canvas';
import { ElementForm } from './ElementForm';
import { useDraftPreview } from './useDraftPreview';
import {
  elementLabel,
  newElement,
  removeElement,
  withElement,
  type ElementType,
  type Layout,
  type Source,
} from './layout';

interface Props {
  template: LabelTemplate;
  onClose: () => void;
}

const TYPES: ElementType[] = ['text', 'qr', 'barcode', 'line'];

function profileAcceptsMedium(profile: ManagedPrinterProfile, width: number, height: number): boolean {
  const settings = profile.settings;
  if (settings.driver === 'tspl_classic' || settings.driver === 'system_pdf') return width <= settings.head_width_mm;
  if (settings.driver === 'niimbot_ble') return height >= 8 && height <= settings.max_width_mm && width <= settings.max_height_mm;
  return height <= settings.max_width_mm && width <= settings.max_height_mm;
}

/**
 * Laying out one label.
 *
 * Two pictures side by side, and they are not the same picture. The canvas is
 * the handle — blocks that can be dragged, sized to the millimetre grid. The
 * preview is the truth: the server's raster, at the printer's own resolution,
 * with the fonts and the thresholding that decide whether a name fits.
 *
 * Editing a copy of the layout rather than the saved one, with an explicit
 * save. A label is consumable and a roll is bought by the metre; an autosaving
 * designer would mean somebody else's next print run comes out in whatever
 * state this screen was left in.
 */
export function LabelDesigner({ template, onClose }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [layout, setLayout] = useState<Layout>(
    () => (template.layout?.elements ? template.layout : { elements: [] }) as Layout,
  );
  const [nameRu, setNameRu] = useState(template.name_ru);
  const [nameEn, setNameEn] = useState(template.name_en);
  const [width, setWidth] = useState(template.media_width_mm);
  const [height, setHeight] = useState(template.media_height_mm);
  const [selected, setSelected] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [bindingError, setBindingError] = useState<string | null>(null);

  const media = { width_mm: width, height_mm: height };
  const preview = useDraftPreview({
    layout,
    media_width_mm: width,
    media_height_mm: height,
    dpi: template.dpi,
    entityKind: template.entity_kind,
  });
  const profiles = useQuery({
    queryKey: ['labels', 'printer-profiles'],
    queryFn: () => listPrinterProfiles(),
  });

  const save = useMutation({
    mutationFn: () =>
      updateTemplate(template.id, {
        name_ru: nameRu.trim(),
        name_en: nameEn.trim() || nameRu.trim(),
        layout,
        media_width_mm: width,
        media_height_mm: height,
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['labels', 'templates'] });
    },
    onError: (exc: unknown) => {
      setSaved(false);
      setError(exc instanceof ApiError ? exc.code : 'unknown');
    },
  });

  const bind = useMutation({
    mutationFn: ({ profile, linked }: { profile: ManagedPrinterProfile; linked: boolean }) =>
      replacePrinterProfileTemplates(
        profile.id,
        linked
          ? profile.template_ids.filter((id) => id !== template.id)
          : [...profile.template_ids, template.id],
      ),
    onSuccess: (profile) => {
      setBindingError(null);
      queryClient.setQueryData<ManagedPrinterProfile[]>(['labels', 'printer-profiles'], (items) =>
        items?.map((item) => item.id === profile.id ? profile : item),
      );
      void queryClient.invalidateQueries({ queryKey: ['labels', 'printer-profiles'] });
    },
    onError: (exc: unknown) => {
      setBindingError(exc instanceof Error ? exc.message : t('labels.designer.printerBindFailed'));
    },
  });

  const change = (next: Layout) => {
    setLayout(next);
    setSaved(false);
  };

  const add = (type: ElementType) => {
    const defaultSource: Source | undefined = template.entity_kind === 'freeform' ? 'field_1' : undefined;
    change({ ...layout, elements: [...layout.elements, newElement(type, media, defaultSource)] });
    setSelected(layout.elements.length);
  };
  const savedMedium = width === template.media_width_mm && height === template.media_height_mm;

  return (
    <section className="designer">
      <header className="designer__head">
        {/* The name is a field, not a heading. A copy arrives called
            "… (копия)" and the only screen that could fix that was this one,
            which showed the name and would not let anybody touch it. */}
        <div className="designer__names">
          <label>
            {t('labels.designer.nameRu')}
            <input value={nameRu} onChange={(event) => {
              setNameRu(event.target.value);
              setSaved(false);
            }} />
          </label>
          <label>
            {t('labels.designer.nameEn')}
            <input value={nameEn} onChange={(event) => {
              setNameEn(event.target.value);
              setSaved(false);
            }} />
          </label>
          {/* Read-only, and shown because it is not decoration: this string is
              what a write-off says in the ledger of the roll it spent. */}
          <span className="hint">{template.code}</span>
        </div>
        <div className="designer__head-actions">
          {/* A template with no name is a blank line in the print dialog. */}
          <button
            type="button"
            className="primary"
            disabled={save.isPending || !nameRu.trim()}
            onClick={() => save.mutate()}
          >
            {t('common.save')}
          </button>
          <button type="button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </header>

      {/* Said before anything is dragged, not after the save is refused: a
          shipped template can be laid out for as long as you like and will not
          keep any of it. */}
      {template.is_seeded && <p className="panel__hint">{t('labels.designer.seededHint')}</p>}
      {error && (
        <p className="panel__error">{t(`labels.errors.${error}`, t('labels.errors.unknown'))}</p>
      )}
      {saved && <p className="panel__hint">{t('labels.designer.saved')}</p>}

      <div className="designer__body">
        <div className="designer__left">
          <div className="designer__media">
            <label>
              {t('labels.designer.mediaWidth')}
              <input
                type="number"
                step={0.5}
                min={1}
                value={width}
                onChange={(event) => {
                  setWidth(Number(event.target.value) || 1);
                  setSaved(false);
                }}
              />
            </label>
            <label>
              {t('labels.designer.mediaHeight')}
              <input
                type="number"
                step={0.5}
                min={1}
                value={height}
                onChange={(event) => {
                  setHeight(Number(event.target.value) || 1);
                  setSaved(false);
                }}
              />
            </label>
            <span className="hint">{template.dpi} dpi</span>
          </div>

          <Canvas
            layout={layout}
            media={media}
            selected={selected}
            onSelect={setSelected}
            onChange={change}
          />

          <div className="designer__add">
            {TYPES.map((type) => (
              <button key={type} type="button" onClick={() => add(type)}>
                + {t(`labels.designer.types.${type}`)}
              </button>
            ))}
          </div>

          <section className="designer__profiles">
            <h4>{t('labels.designer.printers')}</h4>
            <p className="hint">{t('labels.designer.printersHint')}</p>
            {!savedMedium && <p className="panel__hint">{t('labels.designer.saveMediumFirst')}</p>}
            {profiles.isLoading && <p className="hint">{t('common.loading')}</p>}
            {(profiles.data ?? []).map((profile) => {
              const linked = profile.template_ids.includes(template.id);
              const compatible = profileAcceptsMedium(profile, template.media_width_mm, template.media_height_mm);
              const disabled = bind.isPending || !savedMedium || (!compatible && !linked);
              return <label className="designer__profile" key={profile.id}>
                <input
                  type="checkbox"
                  checked={linked}
                  disabled={disabled}
                  onChange={() => bind.mutate({ profile, linked })}
                />
                <span>{profile.name}</span>
                <small>{profile.model}{!compatible && ` · ${t('labels.designer.printerIncompatible')}`}</small>
              </label>;
            })}
            {!profiles.isLoading && (profiles.data ?? []).length === 0 && <p className="panel__hint">{t('labels.designer.noPrinters')}</p>}
            {bindingError && <p className="panel__error">{bindingError}</p>}
          </section>

          {/* The list is how a block hidden underneath another one is reached —
              on a 12 mm tape they overlap constantly. */}
          <ul className="designer__list">
            {layout.elements.map((element, index) => (
              <li key={index}>
                <button
                  type="button"
                  className={selected === index ? 'is-active' : undefined}
                  onClick={() => setSelected(index)}
                >
                  <span>{t(`labels.designer.types.${element.type}`)}</span>
                  <em>{elementLabel(element)}</em>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="designer__right">
          <h4>{t('labels.designer.preview')}</h4>
          <div className="designer__preview">
            {preview.src && (
              <img
                src={preview.src}
                alt={t('labels.preview')}
                style={{ aspectRatio: `${width} / ${height}` }}
              />
            )}
            {preview.pending && <span className="hint">{t('common.loading')}</span>}
          </div>
          {/* The previous raster stays on screen underneath this: half of these
              failures are a block passing over the edge on its way somewhere
              legal, and blanking the picture takes away what is being compared
              against. */}
          {preview.error && (
            <p className="panel__error">
              {t(`labels.errors.${preview.error}`, t('labels.errors.unknown'))}
            </p>
          )}
          <p className="hint">{t('labels.designer.previewHint')}</p>

          {selected !== null && layout.elements[selected] ? (
            <ElementForm
              element={layout.elements[selected]}
              entityKind={template.entity_kind}
              onChange={(element) => change(withElement(layout, selected, element))}
              onRemove={() => {
                change(removeElement(layout, selected));
                setSelected(null);
              }}
            />
          ) : (
            <p className="panel__hint">{t('labels.designer.pickBlock')}</p>
          )}
        </div>
      </div>
    </section>
  );
}
